import React, { Component } from 'react';
import PropTypes from 'prop-types';
import * as d3 from 'd3';
import { isEqual, get } from 'lodash';
import * as annotationOps from '../../../api/Annotations/annotation-operations';
import * as templateOps from '../../../api/AnnotationTemplates/annotation-template-operations';

import { usToDDHHMMSS } from '../../../modules/dates';
import useTimeStore from '../ZustandProvider';

import { LIBRARY_ICON_TYPES } from '../../../api/AnnotationTemplates/AnnotationTemplates';

import Styles from './styles';

const COL_PADDING = 15; // The padding of a Col component in px
const HEIGHT = 50; // The height of the div container element
const MARGIN_LEFT = 20; // The left separation between div container and svg elements
const MARGIN_RIGHT = 20; // The right separation between div container and svg elements
const TOOLTIP_OFFSET = 2.45; // An offset in pixels to align the tooltip
const TOOLTIP_WIDTH = 200; // The initial width of the tooltip
const CONTROL_POINT_TOOLTIP_WIDTH = 62.14; // The initial width of the control points tooltip
const TOOLTIP_ARROW_WIDTH = 12.8; // The width of the tooltip arrow
const ICON_WIDTH = 20; // The width of the icon container element
const ICON_HEIGHT = 20; // The height of the icon container element
const X_OFFSET = 13; // The offset along the x axis to align the annotation icons
const Y_OFFSET = -4; // The offset along the y axis to align the annotation icons
const SELECTOR_RADIUS = 7.5; // The time control handler radius in pixels

const triangleLeft = {
  draw(context, size) {
    const y = -Math.sqrt(size / (Math.sqrt(3) * 3));
    const c = context;
    c.moveTo(0, -y * 2);
    c.lineTo(Math.sqrt(3) * y, y);
    c.lineTo(-Math.sqrt(3) * y, y);
    c.closePath();
  }
};

const leftTrgl = d3
  .symbol()
  .type(triangleLeft)
  .size(150);

class Slider extends Component {
  x = {};

  xAxis = {};

  axis = {};

  svg = {};

  slider = {};

  lineTrack = {};

  lineTrackInset = {};

  n = 0;

  tickNumber = 10;

  selector = {};

  dragRed = {};

  annotationSymbols = [];

  timeTooltipWrapper = {};

  timeTooltip = {};

  timeTooltipInner = {};

  tooltipWrapper = {};

  tooltip = {};

  tooltipInner = {};

  widthNotesComponent = 530;

  componentDidMount() {
    this.init();
    window.addEventListener('resize', this.print);
  }

  componentDidUpdate(prevProps) {
    const {
      domain: prevDomain,
      videoTime: prevVideoTime,
      annotations: prevAnnotations,
      controlPoints: prevControlPoints
    } = prevProps;
    const {
      domain,
      videoTime,
      annotations,
      controlPoints,
      isNotesComponent,
      isMobile
    } = this.props;

    if (isNotesComponent) {
      const { drawerWidth } = this.props;
      this.widthNotesComponent = !isMobile
        ? (drawerWidth * window.innerWidth) / 100 - (MARGIN_LEFT + MARGIN_RIGHT) * 2
        : window.innerWidth / 2 + (MARGIN_LEFT + MARGIN_RIGHT) * 2;
      this.print();
    }

    if (domain !== prevDomain) {
      this.print();
    }
    if (videoTime !== prevVideoTime) {
      this.sliderPrint(true);
    }
    if (!isEqual(annotations, prevAnnotations)) {
      this.annotationsPrint();
    }
    if (!isEqual(controlPoints, prevControlPoints)) {
      this.controlPointsPrint();
    }
    if (isNotesComponent) {
      const { drawerWidth } = this.props;
      this.prevDrawerWidth = drawerWidth;
    }
  }

  componentWillUnmount() {
    this.x = null;
    this.xAxis = null;
    this.axis = null;
    this.svg = null;
    this.slider = null;
    this.lineTrack = null;
    this.lineTrackInset = null;
    this.linePlayedVideo = null;
    this.n = null;
    this.tickNumber = null;
    this.selector = null;
    this.dragRed = null;
    this.annotationSymbols = null;
    this.tooltipWrapper = null;
    this.tooltip = null;
    this.tooltipInner = null;
    window.removeEventListener('resize', this.print);
  }

  onDragStart(x) {
    const {
      isVideoVisualizer,
      isVideoSynchronizer,
      changeVideoContainerStateProperty
    } = this.props;
    this.changeTime(x);
    let propertyToChange = '';
    if (isVideoVisualizer) propertyToChange = 'selectedAnnotation';
    if (isVideoSynchronizer) propertyToChange = 'selectedControlPoint';
    changeVideoContainerStateProperty(propertyToChange, {});
  }

  setTooltipWidth(tooltipRight, tooltipLeft) {
    if (tooltipRight - TOOLTIP_WIDTH / 2 < 0) {
      return 2 * tooltipRight;
    }
    if (tooltipLeft - TOOLTIP_WIDTH / 2 < 0) {
      return 2 * tooltipLeft;
    }
    return TOOLTIP_WIDTH;
  }

  showTooltip = (tooltipName, duration = 200) => {
    this[tooltipName]
      .transition()
      .duration(duration)
      .style('opacity', 0.9);
  };

  hideTooltip = (tooltipName, duration = 200) => {
    this[tooltipName]
      .transition()
      .duration(duration)
      .style('opacity', 0);
  };

  moveTimeTooltip = xMouse => {
    const xMax = this.x.range()[1];
    const xMin = this.x.range()[0];
    let xToUse = 0;
    if (xMin <= xMouse && xMouse <= xMax) {
      xToUse = xMouse;
    } else if (Math.abs(xMin - xMouse) < SELECTOR_RADIUS) {
      xToUse = xMin;
    } else if (Math.abs(xMouse - xMax) < SELECTOR_RADIUS) {
      xToUse = xMax;
    } else {
      this.hideTooltip('timeTooltip', 0);
    }
    this.timeTooltipWrapper.style('right', `${xMax - xToUse + MARGIN_RIGHT / 2}px`);
    this.timeTooltipInner.html(`${usToDDHHMMSS(this.x.invert(xToUse))}`);
    this.timeTooltipArrow.style('right', `${3.75}px`);
  };

  print = () => {
    const { isVideoVisualizer, isVideoSynchronizer } = this.props;
    this.sliderPrint();
    if (isVideoVisualizer) {
      this.annotationsPrint();
    }
    if (isVideoSynchronizer) {
      this.controlPointsPrint();
    }
  };

  changeTime(x) {
    const { changeVideoContainerStateProperty } = this.props;
    this.selector.attr('cx', this.x(this.x.invert(x)));
    this.n = this.x.invert(this.selector.attr('cx'));
    this.moveTimeTooltip(x);
    changeVideoContainerStateProperty('updateVideo', true);
    changeVideoContainerStateProperty('videoTime', this.n);
  }

  init() {
    const { domain, changeVideoContainerStateProperty } = this.props;

    this.svg = d3
      .select('#SliderSvg')
      .on('mouseover', () => {
        const xMouse = d3.mouse(d3.select('#SliderSvg').node())[0] - MARGIN_LEFT;
        const xMax = this.x.range()[1];
        const xMin = this.x.range()[0];
        if (xMouse > xMin - SELECTOR_RADIUS || xMouse < xMax + SELECTOR_RADIUS) {
          this.showTooltip('timeTooltip');
        }
      })
      .on('mousemove', () => {
        const xMouse = d3.mouse(d3.select('#SliderSvg').node())[0] - MARGIN_LEFT;
        this.moveTimeTooltip(xMouse);
      })
      .on('mouseout', () => {
        this.hideTooltip('timeTooltip');
      });

    this.updateDimensions();

    this.x = d3.scaleLinear().domain([0, domain]);

    this.xAxis = d3.axisBottom(this.x);

    this.slider = this.svg.append('g').attr('class', 'slider');

    this.axis = this.slider.append('g').attr('class', 'axis');

    this.dragRed = d3
      .drag()
      .on('start.interrupt', () => {
        this.slider.interrupt();
      })
      .on('start', () => {
        this.onDragStart(d3.event.x);
      })
      .on('drag', () => {
        this.changeTime(d3.event.x);
      })
      .on('end', () => changeVideoContainerStateProperty('updateVideo', false));

    this.lineTrack = this.slider
      .append('line')
      .attr('class', 'track')
      .call(this.dragRed);

    this.lineTrackInset = this.lineTrack
      .select(function a() {
        return this.parentNode.appendChild(this.cloneNode(true));
      })
      .attr('class', 'track-inset')
      .call(this.dragRed);

    this.linePlayedVideo = this.lineTrack
      .select(function a() {
        return this.parentNode.appendChild(this.cloneNode(true));
      })
      .attr('class', 'track-played')
      .call(this.dragRed);

    this.selector = this.slider
      .append('circle')
      .attr('class', 'selector')
      .attr('cy', 0)
      .attr('r', SELECTOR_RADIUS)
      .call(this.dragRed);

    this.timeTooltipWrapper = d3
      .select('#SliderWrapper')
      .append('div')
      .attr('class', 'tooltip-wrapper')
      .style('bottom', '42.5px');

    this.timeTooltip = this.timeTooltipWrapper
      .append('div')
      .attr('class', 'tooltip bs-tooltip-top')
      .attr('role', 'tooltip');
    this.timeTooltipArrow = this.timeTooltip.append('div').attr('class', 'arrow');
    this.timeTooltipInner = this.timeTooltip.append('div').attr('class', 'tooltip-inner');

    this.tooltipWrapper = d3
      .select('#SliderWrapper')
      .append('div')
      .attr('class', 'tooltip-wrapper')
      .style('bottom', '45px');

    this.tooltip = this.tooltipWrapper
      .append('div')
      .attr('class', 'tooltip marker-tooltip bs-tooltip-top')
      .attr('role', 'tooltip')
      .style('opacity', 0)
      .on('mouseover', () => {
        this.showTooltip('tooltip');
      })
      .on('mouseout', () => {
        this.hideTooltip('tooltip');
      });
    this.tooltipArrow = this.tooltip.append('div').attr('class', 'arrow');
    this.tooltipInner = this.tooltip.append('div').attr('class', 'tooltip-inner');

    this.print();
  }

  updateDimensions() {
    this.width = window.innerWidth / 2 - 2 * COL_PADDING - MARGIN_LEFT - MARGIN_RIGHT;
    if (window.innerWidth < 768) {
      this.tickNumber = 5;
      this.width = window.innerWidth - 2 * COL_PADDING - MARGIN_LEFT - MARGIN_RIGHT;
    } else if (window.innerWidth < 992) {
      this.tickNumber = 5;
    } else if (window.innerWidth < 1200) {
      this.tickNumber = 5;
    } else {
      this.tickNumber = 10;
    }
  }

  annotationsPrint() {
    const { annotations, domain, changeVideoContainerStateProperty } = this.props;

    d3.selectAll('.annotation-icon').remove();

    annotations.forEach(annotation => {
      let htmlElementForSvg = '';
      const annotationTemplate = annotationOps.getTemplate(annotation);
      const libraryIcons = templateOps.getLibraryIcon(annotationTemplate);
      if (libraryIcons === LIBRARY_ICON_TYPES.FONTAWESOME) {
        htmlElementForSvg = `<i class="fa fa-${templateOps.getIcon(
          annotationOps.getTemplate(annotation)
        )}"></i>`;
      }

      if (libraryIcons === LIBRARY_ICON_TYPES.SVG) {
        htmlElementForSvg = `<img width=${ICON_WIDTH} height=${ICON_HEIGHT} color="#9400d3" src="${templateOps.getIconSvg(
          annotationOps.getTemplate(annotation)
        )}"/>`;
      }

      const tooltipInnerText = templateOps.getName(annotationTemplate);
      const tooltipRight =
        this.x(domain) -
        this.x(annotationOps.getVideoTime(annotation)) +
        MARGIN_RIGHT +
        TOOLTIP_OFFSET;
      const tooltipLeft =
        this.x(annotationOps.getVideoTime(annotation)) + MARGIN_LEFT - TOOLTIP_OFFSET;

      this.svg
        .append('svg:foreignObject')
        .attr('width', `${ICON_WIDTH}`)
        .attr('height', `${ICON_HEIGHT}`)
        .attr('class', 'annotation-icon')
        .html(htmlElementForSvg)
        .style('color', 'darkviolet')
        .style('cursor', 'pointer')
        .attr(
          'transform',
          () =>
            `translate(${this.x(annotationOps.getVideoTime(annotation)) + X_OFFSET}, ${Y_OFFSET})`
        )
        .on('mousedown', () => {
          this.changeTime(this.x(annotationOps.getVideoTime(annotation)));
          changeVideoContainerStateProperty('selectedAnnotation', annotation);
        })
        .on('mouseup', () => changeVideoContainerStateProperty('updateVideo', false))
        .on('mouseover', () => {
          this.tooltipArrow.style(
            'left',
            `${(this.setTooltipWidth(tooltipRight, tooltipLeft) - TOOLTIP_ARROW_WIDTH) / 2 +
              TOOLTIP_OFFSET}px`
          );
          this.tooltipInner.html(`<strong>${tooltipInnerText}</strong>`);
          this.tooltipWrapper.style('right', `${tooltipRight}px`);
          this.tooltip.style('width', `${this.setTooltipWidth(tooltipRight, tooltipLeft)}px`);
          this.showTooltip('tooltip');
        })
        .on('mouseout', () => this.hideTooltip('tooltip'));
    });
  }

  controlPointsPrint() {
    const { controlPoints, domain, changeVideoContainerStateProperty } = this.props;

    d3.selectAll('.control-point-symbol').remove();

    controlPoints.forEach((controlPoint, index, array) => {
      const tooltipRight =
        this.x(domain) -
        this.x(get(controlPoint, 'properties.timeUS', 0)) +
        MARGIN_RIGHT +
        TOOLTIP_OFFSET -
        0.5;

      this.svg
        .append('path')
        .attr('class', 'control-point-symbol')
        .attr('d', leftTrgl)
        .style('fill', 'darkviolet')
        .style('stroke', 'none')
        .attr(
          'transform',
          () =>
            `translate(${this.x(get(controlPoint, 'properties.timeUS', 0)) + MARGIN_LEFT + 0.5}, 0)`
        )
        .on('mousedown', () => {
          this.changeTime(this.x(get(controlPoint, 'properties.timeUS', 0)));
          if (index !== 0 && index !== array.length - 1) {
            changeVideoContainerStateProperty('selectedControlPoint', controlPoint);
          }
        })
        .on('mouseup', () => changeVideoContainerStateProperty('updateVideo', false))
        // In the case of the control points, the tooltip's width will not be adapted, since it
        // would lead to illegibility of the message it contains for the first and last control
        // points and currently, the tooltip's overflowing in those cases is admissible.
        .on('mouseover', () => {
          this.tooltipArrow.style(
            'left',
            `${(CONTROL_POINT_TOOLTIP_WIDTH - TOOLTIP_ARROW_WIDTH) / 2 + TOOLTIP_OFFSET}px`
          );
          this.tooltipInner.html(`<strong>Control<br />Point<br />#${index}</strong>`);
          this.tooltipWrapper.style('right', `${tooltipRight}px`);
          this.showTooltip('tooltip');
        })
        .on('mouseout', () => this.hideTooltip('tooltip'));
    });
  }

  sliderPrint(onlySelector = false) {
    const { domain, videoTime, isNotesComponent } = this.props;

    if (!onlySelector || isNotesComponent) {
      this.updateDimensions();

      this.x
        .domain([0, domain])
        .range([0, !isNotesComponent ? this.width : this.widthNotesComponent])
        .clamp(true);

      this.xAxis.ticks(this.tickNumber).tickFormat(d => usToDDHHMMSS(d));

      this.slider.attr('transform', `translate(${MARGIN_LEFT},${HEIGHT / 2 - 15})`);

      this.axis.call(this.xAxis).lower();

      this.lineTrack.attr('x1', this.x.range()[0]).attr('x2', this.x.range()[1]);

      this.lineTrackInset.attr('x1', this.x.range()[0]).attr('x2', this.x.range()[1]);

      this.slider.selectAll('text').style('text-anchor', 'middle');
    }

    this.linePlayedVideo.attr('x1', this.x.range()[0]).attr('x2', this.x(videoTime));

    this.selector.attr('cx', this.x(videoTime));
  }

  render() {
    return (
      <Styles.Slider id="SliderWrapper">
        <Styles.Svg className="SliderSvg" id="SliderSvg" />
      </Styles.Slider>
    );
  }
}

Slider.defaultProps = {
  annotations: [],
  controlPoints: [],
  isVideoSynchronizer: false,
  isVideoVisualizer: false,
  changeVideoContainerStateProperty: () => {},
  isNotesComponent: false,
  isMobile: false,
  drawerWidth: 0
};

Slider.propTypes = {
  annotations: PropTypes.array,
  controlPoints: PropTypes.array,
  domain: PropTypes.number.isRequired,
  isVideoSynchronizer: PropTypes.bool,
  isVideoVisualizer: PropTypes.bool,
  videoTime: PropTypes.number.isRequired,
  changeVideoContainerStateProperty: PropTypes.func,
  isNotesComponent: PropTypes.bool,
  isMobile: PropTypes.bool,
  drawerWidth: PropTypes.number
};
const withStore = props => {
  const drawerWidth = useTimeStore(state => state.drawerWidth);

  return <Slider {...props} drawerWidth={drawerWidth} />;
};
export default withStore;
